/**
 * Sync Manager per la PWA Letturisti
 * Gestisce la sincronizzazione tra IndexedDB locale e server
 */
import pwaApi from './pwaApi';
import offlineDb from './offlineDb';

class SyncManager {
  constructor() {
    this._syncing = false;
    this._listeners = new Set();
    this._retryTimeout = null;

    // Ascolta eventi online/offline
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.onOnline());
      window.addEventListener('offline', () => this.onOffline());
    }
  }

  // Registra listener per aggiornamenti stato sync
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  _notify(event) {
    this._listeners.forEach(fn => fn(event));
  }

  get isOnline() {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  async onOnline() {
    this._notify({ type: 'online' });
    // Tenta sync automatica dopo 2 secondi (attendi connessione stabile)
    if (this._retryTimeout) clearTimeout(this._retryTimeout);
    this._retryTimeout = setTimeout(() => this.syncAll(), 2000);
  }

  onOffline() {
    this._notify({ type: 'offline' });
    if (this._retryTimeout) {
      clearTimeout(this._retryTimeout);
      this._retryTimeout = null;
    }
  }

  /**
   * Scarica dati dal server e salva in IndexedDB
   */
  async downloadData() {
    if (!this.isOnline) throw new Error('Offline');
    
    this._notify({ type: 'downloading' });
    try {
      const result = await pwaApi.getSyncData();
      if (result.success) {
        await offlineDb.syncFromServer(result.data);
        this._notify({ type: 'downloaded', data: result.data });
        return result.data;
      }
      throw new Error('Sync download fallita');
    } catch (error) {
      this._notify({ type: 'download-error', error: error.message });
      throw error;
    }
  }

  /**
   * Carica letture pendenti sul server
   */
  async uploadPendingReadings() {
    if (!this.isOnline) throw new Error('Offline');
    if (this._syncing) return;

    this._syncing = true;
    this._notify({ type: 'uploading' });

    try {
      const pending = await offlineDb.getPendingReadings();
      if (pending.length === 0) {
        this._notify({ type: 'upload-complete', count: 0 });
        return { synced: 0 };
      }

      // Invia come batch
      const batches = pending.map(p => ({
        readings: p.readings,
        operatore: p.operatore,
        livello_impianto: p.livello_impianto,
        gps: p.gps,
        timestamp: p.created_at
      }));

      const result = await pwaApi.uploadSyncBatches(batches);

      if (result.success) {
        // Segna come sincronizzate
        for (const p of pending) {
          await offlineDb.markPendingAsSynced(p.localId);
        }
        this._notify({ type: 'upload-complete', count: pending.length, result: result.data });
        return { synced: pending.length, result: result.data };
      }

      throw new Error(result.message || 'Upload fallito');
    } catch (error) {
      this._notify({ type: 'upload-error', error: error.message });
      throw error;
    } finally {
      this._syncing = false;
    }
  }

  /**
   * Carica foto pendenti sul server
   */
  async uploadPendingPhotos() {
    if (!this.isOnline) throw new Error('Offline');

    const pendingPhotos = await offlineDb.getPendingPhotos();
    if (pendingPhotos.length === 0) return { synced: 0 };

    this._notify({ type: 'uploading' });

    // Raggruppa per (plant_id, meter_id, tipo) per batch upload
    const groups = {};
    for (const photo of pendingPhotos) {
      const key = `${photo.plant_id}_${photo.meter_id || 'plant'}_${photo.tipo}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(photo);
    }

    let syncedCount = 0;
    for (const [, photos] of Object.entries(groups)) {
      try {
        const formData = new FormData();
        for (const photo of photos) {
          // Converti base64 → Blob
          const arr = photo.base64.split(',');
          const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
          const bstr = atob(arr[1]);
          const u8arr = new Uint8Array(bstr.length);
          for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
          const blob = new Blob([u8arr], { type: mime });
          formData.append('photos', blob, photo.filename);
        }
        const first = photos[0];
        if (first.gps_latitude) formData.append('gps_latitude', first.gps_latitude);
        if (first.gps_longitude) formData.append('gps_longitude', first.gps_longitude);
        if (first.gps_accuracy) formData.append('gps_accuracy', first.gps_accuracy);

        if (first.tipo === 'impianto' || !first.meter_id) {
          await pwaApi.uploadPlantPhotos(first.plant_id, formData);
        } else {
          await pwaApi.uploadMeterPhotos(first.meter_id, formData);
        }

        // Marca come sincronizzate
        for (const photo of photos) {
          await offlineDb.markPendingPhotoAsSynced(photo.localId);
          syncedCount++;
        }
      } catch (err) {
        console.error('Errore upload foto pendenti:', err);
      }
    }

    if (syncedCount > 0) {
      this._notify({ type: 'upload-complete', count: syncedCount });
    }
    return { synced: syncedCount };
  }

  /**
   * Sincronizzazione completa: upload pendenti + download dati aggiornati
   */
  async syncAll() {
    if (!this.isOnline || this._syncing) return;

    try {
      // Prima upload le letture pendenti
      await this.uploadPendingReadings();
      // Poi upload foto pendenti
      await this.uploadPendingPhotos();
      // Poi scarica dati aggiornati
      await this.downloadData();
      this._notify({ type: 'sync-complete' });
    } catch (error) {
      console.error('Errore sync completa:', error);
      this._notify({ type: 'sync-error', error: error.message });
    }
  }

  /**
   * Pulisci letture e foto già sincronizzate
   */
  async cleanup() {
    await offlineDb.clearSyncedReadings();
    await offlineDb.clearSyncedPhotos();
    this._notify({ type: 'cleanup-complete' });
  }
}

// Singleton
const syncManager = new SyncManager();
export default syncManager;
